import React from "react";
import { Images } from "../../../../../../utils/imageConstants";
import type { IMatchCommentary, InningScorecard, IMatchDetails } from "./types/matchDetails.d";
import { playerIdFromMatchPerson, useOpenPlayerProfile } from "./useOpenPlayerProfile";
import UpcomingMatchPointsTableTab from "./upcomingMatchesDetails/UpcomingMatchPointsTableTab";
import UpcomingMatchRecentPerformance from "./upcomingMatchesDetails/UpcomingMatchRecentPerformance";
import UpcomingMatchPlayersList from "./upcomingMatchesDetails/UpcomingMatchPlayersList";
import { getSeasonYear } from "../../../../../../helper/helper";
import { getMatchDaysText, getMatchHoursOfPlay, getMatchOfficialDisplayName, getMatchPersonDisplayName, getMatchVenueText, getMatchStatusDisplayLabel } from "./matchDetailDisplay";

type MatchSummaryProps = {
  setMatchTab: React.Dispatch<React.SetStateAction<string>>;
  scores: InningScorecard[] | undefined;
  commentaryData: IMatchCommentary[] | undefined;
  matchStatus?: string;
  matchSummary?: IMatchDetails;
};

const MatchSummary = ({ setMatchTab, scores = [], commentaryData = [], matchStatus, matchSummary }: MatchSummaryProps) => {
  const openPlayerProfile = useOpenPlayerProfile();
  const isUpcoming = matchStatus === "UPCOMING" || matchStatus === "SCHEDULED";
  const isLive = matchStatus === "IN_PROGRESS";
  const venueText = getMatchVenueText(matchSummary);

  const renderScoreSummaryNameCell = (playerId: string | undefined, firstName?: string | null, lastName?: string | null) => {
    const label = [firstName, lastName].filter((p) => p?.trim()).join(" ") || "-";
    const id = playerId?.trim();
    if (!id) {
      return <td className="player-name">{label}</td>;
    }
    return (
      <td className="player-name">
        <button type="button" className="player-name-link" onClick={() => openPlayerProfile(id)}>
          {label}
        </button>
      </td>
    );
  };

  return (
    <div>
      <div className="text-section">
        {isUpcoming ? <p>Match Yet To Begin</p> : <p>Match Center</p>}
        {!isUpcoming && <span>Scores : Ranjith P | Cooms : Alagppan Muthu</span>}
      </div>
      <div className="match-summary-wrapper">
        <div className="scorecard-summary">
          {isUpcoming ? (
            <>
              <UpcomingMatchPointsTableTab matchSummary={matchSummary} />
              <UpcomingMatchRecentPerformance matchSummary={matchSummary} />
              <UpcomingMatchPlayersList matchSummary={matchSummary} />
            </>
          ) : (
            <>
              <p className="scorecard-text">Scorecard Summary</p>
              <div className="first-team-score-wrapper">
                <p>
                  {scores[0]?.battingTeamName} : <span>{` ${scores[0]?.runs}/${scores[0]?.wicket}`}</span>
                  <span className="over">({scores[0]?.over}Overs)</span>
                </p>
                <div className="scorecard-table-container">
                  <div className="batting-section">
                    <table className="scorecard-table">
                      <thead>
                        <tr></tr>
                      </thead>
                      <tbody>
                        {scores[0]?.battingScoreCardResponseList
                          ?.filter((p) => p.status === "STRIKER" || p.status === "NON_STRIKER")
                          .sort((a) => (a.status === "STRIKER" ? -1 : 1))
                          .map((player, index) => (
                            <tr key={player?.playerId || index} className="player-row">
                              {renderScoreSummaryNameCell(player?.playerId, player?.playerFirstName, player?.playerLastName)}
                              <td className="player-runs">
                                {player.run}
                                <span className="over">{`(${player?.balls})`}</span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="bowling-section">
                    <table className="scorecard-table">
                      <thead>
                        <tr></tr>
                      </thead>
                      <tbody>
                        {scores[0]?.bowlingScoreCardResponses?.slice(0, 2)?.map((player, index) => (
                          <tr key={player?.id || index} className="player-row">
                            {renderScoreSummaryNameCell(player?.playerId, player?.playerFirstName, player?.playerLastName)}
                            <td className="player-wickets">
                              {player.wickets}/{player.runs}({player.over})
                            </td>
                            <td className="player-runs"></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {!isLive && (
                <div className="second-team-score-wrapper">
                  <p>
                    {scores[0]?.bowlingTeamName} : {`${scores[1]?.runs}/${scores[1]?.wicket}`} <span className="over">({scores[1]?.over} Overs)</span>
                  </p>
                  <div className="scorecard-table-container">
                    <div className="batting-section">
                      <table className="scorecard-table">
                        <thead>
                          <tr></tr>
                        </thead>
                        <tbody>
                          {scores[1]?.battingScoreCardResponseList?.slice(0, 2)?.map((player, index) => (
                            <tr key={player?.playerId || index} className="player-row">
                              {renderScoreSummaryNameCell(player?.playerId, player?.playerFirstName, player?.playerLastName)}
                              <td className="player-runs">
                                {player?.run}
                                {`(${player.balls})`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bowling-section">
                      <table className="scorecard-table">
                        <thead>
                          <tr></tr>
                        </thead>
                        <tbody>
                          {scores[1]?.bowlingScoreCardResponses?.slice(0, 2)?.map((player, index) => (
                            <tr key={player?.id || index} className="player-row">
                              {renderScoreSummaryNameCell(player?.playerId, player?.playerFirstName, player?.playerLastName)}
                              <td className="player-wickets">
                                {player.wickets}/{player.runs}({player.over})
                              </td>
                              <td className="player-runs"></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              <div className="fill-scrorecard">
                <p
                  onClick={() => {
                    setMatchTab("Scorecard");
                    document.getElementById("match-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  VIEW FULL SCORECARD
                </p>
              </div>
              {commentaryData[0]?.ballsResponseList?.map((item, ind) => {
                const isWicket = item?.wicket?.wicket_id != null;
                return (
                  <div key={ind} className="commentary-section">
                    <div className="commentary-item">
                      <div className="ball-number">
                        <span className="over">
                          {item.overNumber}.{item.ball_number}
                        </span>

                        <>
                          {isWicket && (
                            <div className="wicket-red">
                              <div className="wicket">w</div>
                            </div>
                          )}
                          {!isWicket && (
                            <>
                              {item?.runs_scored === 0 && <span className="ball-indicator dot">•</span>}
                              {item?.runs_scored === 1 && <span className="ball-indicator runs">1</span>}
                              {item?.runs_scored === 2 && <span className="ball-indicator runs">2</span>}
                              {item?.runs_scored === 3 && <span className="ball-indicator runs">3</span>}
                              {item?.runs_scored === 4 && <span className="ball-indicator boundary">4</span>}
                              {item?.runs_scored === 6 && <span className="ball-indicator boundary">6</span>}
                            </>
                          )}
                        </>
                      </div>

                      <div className="commentary-content">
                        <div className="ball-description">
                          <strong>{item?.commentary}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="fill-scrorecard">
                <p
                  onClick={() => {
                    setMatchTab("Commentary");
                    document.getElementById("match-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  READ FULL COMMENTARY
                </p>
              </div>
            </>
          )}
        </div>
        <section className="match-details" aria-labelledby="match-details-title">
          <p className="match-detail-text">Match Details</p>
          <div className="location">
            <p>{venueText}</p>
          </div>
          <div className="match-detail-data">
            <table>
              <tbody>
                <tr>
                  <th scope="row">Toss</th>
                  <td>
                    <span className="right-label">{matchSummary?.tossResult || "-"}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">League</th>
                  <td>
                    <span className="right-label">{matchSummary?.leagueName || "-"}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Season</th>
                  <td>
                    <span className="right-label">{getSeasonYear(matchSummary?.startDate)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Player of The Match</th>
                  <td>
                    <span className="right-label">
                      {(() => {
                        const potm = matchSummary?.playerOfTheMatch;
                        const pid = playerIdFromMatchPerson(potm);
                        const name = getMatchPersonDisplayName(potm);
                        if (!pid) return name;
                        return (
                          <button type="button" className="player-name-link" onClick={() => openPlayerProfile(pid)}>
                            {name}
                          </button>
                        );
                      })()}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Series</th>
                  <td>
                    <span className="right-label">{matchSummary?.tournament?.tournamentName || "-"}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Hours of Play (local time)</th>
                  <td>
                    <span className="right-label">{getMatchHoursOfPlay(matchSummary)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Match days</th>
                  <td>
                    <span className="right-label">{getMatchDaysText(matchSummary)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Umpire</th>
                  <td>
                    <span className="right-label">{getMatchOfficialDisplayName(matchSummary?.umpire ?? matchSummary?.availableUmpireResponse)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Second Umpire</th>
                  <td>
                    <span className="right-label">{getMatchOfficialDisplayName(matchSummary?.umpireTwo)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Third Umpire</th>
                  <td>
                    <span className="right-label">{getMatchOfficialDisplayName(matchSummary?.thirdUmpire)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Match Status</th>
                  <td>
                    <span className="right-label">{getMatchStatusDisplayLabel(matchSummary?.matchStatus)}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Result</th>
                  <td>
                    <span className="right-label">{matchSummary?.matchResult || matchSummary?.equation || "-"}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MatchSummary;
